import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";

import { cn } from "@workspace/web-ui/lib/utils";

type VariantType = "default" | "light" | "dark";
type SizeType = "default" | "sm" | "lg";

const overlayVariants = cva(
  "fixed inset-0 z-50 flex items-center justify-center transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-black/20 backdrop-blur-[2px]",
        light: "bg-white/40 backdrop-blur-[1px]",
        dark: "bg-black/40 backdrop-blur-[3px]",
      } satisfies Record<VariantType, string>,
      size: {
        default: "",
        sm: "p-2",
        lg: "p-4",
      } satisfies Record<SizeType, string>,
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const spinnerVariants = cva(
  "flex flex-col items-center gap-2 rounded-2xl p-6 shadow-2xl backdrop-blur-xl",
  {
    variants: {
      variant: {
        default: "bg-white/90",
        light: "bg-white/95",
        dark: "bg-gray-900/90",
      } satisfies Record<VariantType, string>,
      size: {
        default: "p-6",
        sm: "p-4",
        lg: "p-8",
      } satisfies Record<SizeType, string>,
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const ProxyLogo = ({ className }: { className?: string }) => (
  <svg
    width="54"
    height="53"
    viewBox="0 0 54 53"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_loading_logo)">
      <path
        d="M36.7859 10.3599C36.6244 7.03381 35.3562 4.31066 32.7751 2.2119C28.7545 -1.0481 20.2 -1.43518 17.578 6.12156C16.6229 8.87972 16.3876 11.6865 16.4459 14.5439C16.4654 15.5884 16.1036 15.8607 15.1194 15.8023C13.6761 15.7226 12.2231 15.7187 10.7759 15.779C8.63632 15.8724 6.53366 16.275 4.67414 17.4207C0.324882 20.103 -1.32457 26.4537 1.16711 31.3301C2.78544 34.5006 5.20515 36.6344 9.0409 36.8173C11.0132 36.9145 12.9681 36.8834 14.9326 36.792C16.7707 36.7064 16.7669 36.7492 16.6832 38.5289C16.6151 40.2504 16.4693 41.9698 16.6988 43.6952C17.1889 47.3909 18.7353 50.3844 22.3182 51.9229C23.2907 52.3411 24.7029 52.629 26.1189 52.6854V50.573C26.119 50.1195 25.9627 49.6797 25.6764 49.3279C25.3902 48.9761 24.9914 48.7337 24.5473 48.6416H24.5317C23.7751 48.484 22.713 47.9044 22.2229 47.2917C20.6104 45.2824 20.4003 42.9035 20.4995 40.4682C20.589 38.3014 20.7718 36.1404 20.8496 33.9774C20.9021 32.505 20.731 32.398 19.2683 32.5633C16.3506 32.892 13.4232 32.9523 10.4958 33.0846C7.33893 33.2305 4.64301 31.0131 4.18592 27.7025C3.7755 24.6915 4.76945 20.5951 9.54857 19.9824C10.5542 19.8501 11.552 19.8851 12.5518 19.8287C14.9579 19.6906 17.3582 20.1497 19.7682 19.9046C20.4042 19.8404 20.5851 19.5895 20.5695 18.9962C20.5384 17.7922 20.5579 16.5862 20.5579 15.3783C20.4956 13.4954 20.4081 11.6048 20.7835 9.73751C21.2056 7.64068 21.8086 5.60416 23.991 4.68023C27.8423 3.05802 31.9795 5.68974 32.46 9.87366C32.6545 11.5776 32.6331 17.4596 32.6389 19.1343C32.6295 19.4619 33.07 19.5545 33.249 19.28C33.8002 18.4345 34.4716 17.6701 35.0742 16.8624C36.0642 15.5436 36.8248 14.2112 36.8248 12.6202C36.8248 11.7273 36.8131 10.9124 36.7859 10.3599Z"
        fill="#6AC9FD"
      />
      <path
        d="M44.1257 33.492C42.4646 33.8247 40.784 33.8811 39.0957 33.8811C38.3176 33.8811 38.0375 33.5543 37.95 32.8307C37.7555 31.1618 37.4131 29.5046 37.4345 28.2694C37.382 25.4237 37.6505 23.1285 39.6442 21.4616C42.7564 18.8765 47.8836 20.4287 49.4981 24.4181C51.2214 28.6857 48.9496 32.5098 44.1257 33.492ZM53.2852 23.288C51.6435 18.9232 47.374 16.062 42.7816 16.2545C38.5024 16.4296 35.0732 19.2733 33.8808 23.7918C33.1203 26.6881 33.6999 29.6057 33.9256 32.5292L33.9372 32.679C34.1668 35.4897 34.8048 38.2867 34.4099 41.1441C33.9761 44.2562 32.4765 46.9794 29.5724 48.5997C29.4978 48.6418 29.4205 48.6788 29.341 48.7105C28.9826 48.8583 28.677 49.1103 28.4637 49.4339C28.2504 49.7575 28.1392 50.1378 28.1447 50.5253V52.6649C28.6492 52.6535 29.1525 52.6126 29.6522 52.5424C32.5912 52.1534 34.8048 50.1091 36.3803 47.5707C37.9558 45.0323 38.6405 42.1925 38.5977 39.2593C38.5822 38.1583 38.7669 37.8082 39.8912 37.816C40.9468 37.8243 42.0016 37.7593 43.0481 37.6215C44.1489 37.4758 45.2382 37.2535 46.3081 36.9563C52.3866 35.2446 55.5046 29.2109 53.2852 23.288Z"
        fill="#3085D4"
      />
    </g>
    <defs>
      <clipPath id="clip0_loading_logo">
        <rect width="54" height="52.6851" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

interface LoadingOverlayProps extends VariantProps<typeof overlayVariants> {
  isLoading: boolean;
  message?: string;
  className?: string;
  spinnerClassName?: string;
  showLoader?: boolean;
}

export const LoadingOverlay = ({
  isLoading,
  message,
  className,
  spinnerClassName,
  variant,
  size,
  showLoader = true,
}: LoadingOverlayProps) => {
  if (!isLoading) return null;

  if (!showLoader) {
    return (
      <div
        role="alert"
        aria-busy="true"
        className={cn(overlayVariants({ variant, size }), className)}
      />
    );
  }

  const messageTextColor = variant === "dark" ? "text-neutral-200" : "text-neutral-700";

  return (
    <div
      role="alert"
      aria-busy="true"
      aria-label={message || "Loading"}
      className={cn(overlayVariants({ variant, size }), className)}
    >
      <div className={cn(spinnerVariants({ variant, size }), spinnerClassName)}>
        <div className="relative">
          {/* Ambient glow effect */}
          <div
            className={cn(
              "absolute inset-0 rounded-full",
              "bg-gradient-to-br from-[#6AC9FD]/40 to-[#3085D4]/40",
              "scale-150 blur-2xl",
              "animate-[glow_2s_ease-in-out_infinite]"
            )}
            aria-hidden="true"
          />

          {/* Pulsing ring effect */}
          <div
            className={cn(
              "absolute inset-0 rounded-full",
              "border-2 border-[#6AC9FD]/30",
              "animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]"
            )}
            aria-hidden="true"
          />

          {/* Shadow effect */}
          <div
            className={cn(
              "absolute -bottom-4 left-1/2 h-3 w-12",
              "-translate-x-1/2 rounded-full",
              "bg-gradient-to-r from-transparent via-[#3085D4]/20 to-transparent",
              "blur-md",
              "animate-[shadow_2s_ease-in-out_infinite]"
            )}
            aria-hidden="true"
          />

          {/* Animated Logo */}
          <div className="relative">
            <ProxyLogo
              aria-hidden="true"
              className={cn(
                "size-14",
                "drop-shadow-[0_0_15px_rgba(106,201,253,0.5)]",
                "animate-[logoBreath_2s_cubic-bezier(0.4,0,0.6,1)_infinite]"
              )}
            />
          </div>
        </div>

        {message && (
          <span
            className={cn(
              "mt-4 text-sm font-medium tracking-wide",
              "animate-[fadeText_2s_ease-in-out_infinite]",
              messageTextColor
            )}
          >
            {message}
          </span>
        )}
      </div>

      {/* Custom keyframes */}
      <style>{`
        @keyframes logoBreath {
          0%, 100% {
            transform: scale(1) rotate(0deg);
          }
          25% {
            transform: scale(1.15) rotate(45deg);
          }
          50% {
            transform: scale(1.25) rotate(0deg);
          }
          75% {
            transform: scale(1.15) rotate(-45deg);
          }
        }

        @keyframes glow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1.5);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.8);
          }
        }

        @keyframes shadow {
          0%, 100% {
            opacity: 0.3;
            transform: translateX(-50%) scaleX(1);
          }
          50% {
            opacity: 0.5;
            transform: translateX(-50%) scaleX(1.5);
          }
        }

        @keyframes fadeText {
          0%, 100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
